import { useEffect, useState } from "react";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { ShieldCheck, Flag, Lightbulb, Gavel, ScrollText, UserCog, SlidersHorizontal, Check, EyeOff, Trash2, Ban, Clock, RotateCcw } from "lucide-react";

const SUBTABS = [
  ["queue", "File d'attente", Gavel],
  ["reports", "Signalements", Flag],
  ["ideas", "Idées", Lightbulb],
  ["sanctions", "Sanctions", Ban],
  ["log", "Journal", ScrollText],
  ["roles", "Rôles", UserCog],
  ["settings", "Réglages", SlidersHorizontal],
];

const IDEA_STATUS = [["review", "À l'étude"], ["planned", "Prévu"], ["in_progress", "En développement"], ["done", "Disponible"], ["rejected", "Refusé"]];

export default function AdminModeration() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const [sub, setSub] = useState("queue");
  const [queue, setQueue] = useState({ posts: [], answers: [], total: 0 });
  const [reports, setReports] = useState([]);
  const [ideas, setIdeas] = useState([]);
  const [sanctions, setSanctions] = useState({ sanctioned: [], history: [] });
  const [log, setLog] = useState([]);
  const [users, setUsers] = useState([]);
  const [settings, setSettings] = useState(null);

  const loadSub = (s) => {
    if (s === "queue") api.get("/moderation/queue").then(({ data }) => setQueue(data)).catch(() => {});
    if (s === "reports") api.get("/moderation/reports").then(({ data }) => setReports(data.items || [])).catch(() => {});
    if (s === "ideas") api.get("/ideas?sort=votes").then(({ data }) => setIdeas(data.ideas || [])).catch(() => {});
    if (s === "sanctions") api.get("/moderation/sanctions").then(({ data }) => setSanctions(data)).catch(() => {});
    if (s === "log") api.get("/moderation/log").then(({ data }) => setLog(data.log || [])).catch(() => {});
    if (s === "roles") api.get("/admin/users").then(({ data }) => setUsers(data.users || [])).catch(() => {});
    if (s === "settings") api.get("/admin/settings").then(({ data }) => setSettings(data)).catch(() => {});
  };
  useEffect(() => { loadSub(sub); }, [sub]);

  const act = async (fn, msg) => { try { await fn(); toast.success(msg); loadSub(sub); } catch (e) { toast.error(apiErr(e)); } };

  const approve = (t, id) => act(() => api.post(`/moderation/${t}/${id}/approve`), "Approuvé et publié.");
  const hide = (t, id) => act(() => api.post(`/moderation/${t}/${id}/hide`), "Masqué.");
  const del = (t, id) => act(() => api.delete(`/moderation/${t}/${id}`), "Supprimé.");
  const suspend = (uid) => act(() => api.post(`/moderation/users/${uid}/suspend`, { days: 7 }), "Compte suspendu 7 jours.");
  const ban = (uid) => act(() => api.post(`/moderation/users/${uid}/ban`, {}), "Compte banni.");
  const reinstate = (uid) => act(() => api.post(`/moderation/users/${uid}/reinstate`), "Compte réactivé.");
  const setRole = (uid, role) => act(() => api.post(`/admin/users/${uid}/role`, { role }), "Rôle mis à jour.");
  const setIdeaStatus = (id, status) => act(() => api.patch(`/admin/ideas/${id}`, { status }), "Statut mis à jour.");
  const hideIdea = (id) => act(() => api.patch(`/admin/ideas/${id}`, { hidden: true }), "Idée masquée.");
  const delIdea = (id) => act(() => api.delete(`/admin/ideas/${id}`), "Idée supprimée.");

  const saveSettings = () => act(() => api.post("/admin/settings", settings), "Réglages enregistrés.");
  const setS = (k, v) => setSettings((s) => ({ ...s, [k]: v }));

  const ContentCard = ({ item, type }) => (
    <div className="card-surface p-4" data-testid={`mod-item-${item.post_id || item.answer_id}`}>
      <div className="flex items-center justify-between mb-2 text-xs text-white/35">
        <span>{type === "post" ? "Publication" : "Réponse"} · {new Date(item.created_at).toLocaleString("fr-FR")}</span>
        {item.mod_categories?.length > 0 && <span className="text-gold">{item.mod_categories.join(", ")}</span>}
      </div>
      <p className="text-white/85 text-sm mb-3">{item.content}</p>
      <div className="flex gap-2">
        <button data-testid={`approve-${item.post_id || item.answer_id}`} onClick={() => approve(type, item.post_id || item.answer_id)}
          className="inline-flex items-center gap-1.5 text-xs text-emerald-300 border border-emerald-500/30 rounded-full px-3 py-1.5"><Check size={13} /> Approuver</button>
        <button onClick={() => hide(type, item.post_id || item.answer_id)}
          className="inline-flex items-center gap-1.5 text-xs text-white/60 border border-white/15 rounded-full px-3 py-1.5"><EyeOff size={13} /> Masquer</button>
        <button data-testid={`delete-${item.post_id || item.answer_id}`} onClick={() => del(type, item.post_id || item.answer_id)}
          className="inline-flex items-center gap-1.5 text-xs text-red-300 border border-red-500/30 rounded-full px-3 py-1.5"><Trash2 size={13} /> Supprimer</button>
      </div>
    </div>
  );

  return (
    <div data-testid="admin-moderation">
      <div className="flex items-center gap-2 mb-4"><ShieldCheck size={18} className="text-gold" /><h3 className="font-display text-lg">Modération</h3></div>
      <div className="flex flex-wrap gap-2 mb-5">
        {SUBTABS.map(([id, label, Icon]) => (
          (id !== "roles" && id !== "settings") || isAdmin ? (
            <button key={id} data-testid={`modsub-${id}`} onClick={() => setSub(id)}
              className={`inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 text-sm border transition-colors ${sub === id ? "border-gold bg-gold/15 text-gold" : "border-white/10 text-white/60 hover:text-white"}`}>
              <Icon size={14} /> {label}{id === "queue" && queue.total ? ` (${queue.total})` : ""}
            </button>
          ) : null
        ))}
      </div>

      {sub === "queue" && (
        <div className="space-y-3">
          {queue.posts.map((p) => <ContentCard key={p.post_id} item={p} type="post" />)}
          {queue.answers.map((a) => <ContentCard key={a.answer_id} item={a} type="answer" />)}
          {queue.total === 0 && <p className="text-white/30 text-sm">Rien à valider. 👌🏿</p>}
        </div>
      )}

      {sub === "reports" && (
        <div className="space-y-3">
          {reports.map((it) => (
            <div key={it.target_id} className="card-surface p-4" data-testid={`report-item-${it.target_id}`}>
              <div className="flex items-center justify-between mb-2">
                <span className="text-xs px-2 py-0.5 rounded-full bg-red-500/15 text-red-300">{it.count} signalement(s)</span>
                <span className="text-white/35 text-xs">{[...new Set(it.reasons)].join(", ")}</span>
              </div>
              <p className="text-white/85 text-sm mb-3">{it.content?.content || "(contenu supprimé)"}</p>
              <div className="flex gap-2">
                <button onClick={() => hide(it.target_type, it.target_id)} className="text-xs text-white/60 border border-white/15 rounded-full px-3 py-1.5">Masquer</button>
                <button onClick={() => del(it.target_type, it.target_id)} className="text-xs text-red-300 border border-red-500/30 rounded-full px-3 py-1.5">Supprimer</button>
                {it.target_user && <button onClick={() => suspend(it.target_user)} className="text-xs text-amber-300 border border-amber-500/30 rounded-full px-3 py-1.5">Suspendre l'auteur</button>}
              </div>
            </div>
          ))}
          {reports.length === 0 && <p className="text-white/30 text-sm">Aucun signalement en attente.</p>}
        </div>
      )}

      {sub === "ideas" && (
        <div className="space-y-3">
          {ideas.map((i) => (
            <div key={i.idea_id} className="card-surface p-4" data-testid={`mod-idea-${i.idea_id}`}>
              <div className="flex items-start justify-between gap-3 mb-1">
                <p className="text-white font-medium">{i.title} <span className="text-gold text-sm">({i.score})</span></p>
                <div className="flex gap-2 shrink-0">
                  <button onClick={() => hideIdea(i.idea_id)} className="text-white/50 hover:text-white" title="Masquer"><EyeOff size={15} /></button>
                  <button onClick={() => delIdea(i.idea_id)} className="text-red-300" title="Supprimer"><Trash2 size={15} /></button>
                </div>
              </div>
              <p className="text-white/55 text-sm mb-2">{i.description}</p>
              <select value={i.status} onChange={(e) => setIdeaStatus(i.idea_id, e.target.value)} data-testid={`idea-status-${i.idea_id}`}
                className="bg-white/[0.04] border border-white/10 rounded-lg px-3 py-1.5 text-sm">
                {IDEA_STATUS.map(([v, l]) => <option key={v} value={v} className="bg-[#0a0a0a]">{l}</option>)}
              </select>
            </div>
          ))}
          {ideas.length === 0 && <p className="text-white/30 text-sm">Aucune idée.</p>}
        </div>
      )}

      {sub === "sanctions" && (
        <div className="space-y-4">
          <div>
            <p className="text-white/50 text-sm mb-2">Comptes sanctionnés</p>
            <div className="space-y-2">
              {sanctions.sanctioned.map((u) => (
                <div key={u.user_id} className="card-surface p-3 flex items-center justify-between gap-3" data-testid={`sanctioned-${u.user_id}`}>
                  <div className="min-w-0">
                    <p className="text-white/80 text-sm truncate">{u.email}</p>
                    <p className="text-white/35 text-xs">{u.status === "banned" ? "Banni" : `Suspendu jusqu'au ${u.suspended_until ? new Date(u.suspended_until).toLocaleDateString("fr-FR") : "?"}`} · signalements: {u.flags_received || 0}</p>
                  </div>
                  <button onClick={() => reinstate(u.user_id)} className="inline-flex items-center gap-1.5 text-xs text-emerald-300 border border-emerald-500/30 rounded-full px-3 py-1.5"><RotateCcw size={13} /> Réactiver</button>
                </div>
              ))}
              {sanctions.sanctioned.length === 0 && <p className="text-white/30 text-sm">Aucun compte sanctionné.</p>}
            </div>
          </div>
          <div>
            <p className="text-white/50 text-sm mb-2">Historique des sanctions</p>
            <div className="space-y-1.5">
              {sanctions.history.map((h) => (
                <div key={h.log_id} className="text-xs text-white/50 flex items-center gap-2">
                  <Clock size={12} /> {new Date(h.created_at).toLocaleString("fr-FR")} · <span className="text-white/70">{h.action}</span> par {h.actor_email} — {h.detail}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {sub === "log" && (
        <div className="space-y-1.5" data-testid="moderation-log">
          {log.map((l) => (
            <div key={l.log_id} className="text-xs text-white/50 flex flex-wrap items-center gap-2 card-surface px-3 py-2">
              <Clock size={12} className="text-white/30" /> {new Date(l.created_at).toLocaleString("fr-FR")}
              <span className="text-gold">{l.action}</span> · {l.target_type} · par {l.actor_email} {l.detail && `— ${l.detail}`}
            </div>
          ))}
          {log.length === 0 && <p className="text-white/30 text-sm">Journal vide.</p>}
        </div>
      )}

      {sub === "roles" && isAdmin && (
        <div className="space-y-2" data-testid="roles-manager">
          {users.map((u) => (
            <div key={u.user_id} className="card-surface p-3 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-white/80 text-sm truncate">{u.email}</p>
                <p className="text-white/35 text-xs">Confiance: {u.trust_score ?? "—"} · {u.status || "active"}</p>
              </div>
              <select data-testid={`role-select-${u.user_id}`} value={u.role || "user"} onChange={(e) => setRole(u.user_id, e.target.value)}
                className="bg-white/[0.04] border border-white/10 rounded-lg px-3 py-1.5 text-sm">
                <option value="user" className="bg-[#0a0a0a]">Utilisateur</option>
                <option value="editor" className="bg-[#0a0a0a]">Éditeur</option>
                <option value="moderator" className="bg-[#0a0a0a]">Modérateur</option>
                <option value="admin" className="bg-[#0a0a0a]">Administrateur</option>
              </select>
            </div>
          ))}
        </div>
      )}

      {sub === "settings" && isAdmin && settings && (
        <div className="card-surface p-5 space-y-4 max-w-lg" data-testid="mod-settings">
          <label className="flex items-center justify-between gap-3 text-sm">
            <span>Modération activée</span>
            <input data-testid="set-mod-enabled" type="checkbox" checked={!!settings.mod_enabled} onChange={(e) => setS("mod_enabled", e.target.checked)} className="w-5 h-5 accent-[#d4af37]" />
          </label>
          <div>
            <label className="block text-sm text-white/70 mb-1">Niveau de filtrage</label>
            <select data-testid="set-filter-level" value={settings.mod_filter_level || "medium"} onChange={(e) => setS("mod_filter_level", e.target.value)}
              className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm">
              <option value="off" className="bg-[#0a0a0a]">Désactivé</option>
              <option value="low" className="bg-[#0a0a0a]">Faible (règles seules)</option>
              <option value="medium" className="bg-[#0a0a0a]">Moyen (règles + IA ciblée)</option>
              <option value="high" className="bg-[#0a0a0a]">Élevé (IA systématique)</option>
            </select>
          </div>
          <label className="flex items-center justify-between gap-3 text-sm">
            <span>Autoriser les liens dans les posts</span>
            <input type="checkbox" checked={!!settings.mod_allow_links} onChange={(e) => setS("mod_allow_links", e.target.checked)} className="w-5 h-5 accent-[#d4af37]" />
          </label>
          <label className="flex items-center justify-between gap-3 text-sm">
            <span>Nouveaux comptes en modération différée</span>
            <input type="checkbox" checked={!!settings.mod_new_account_review} onChange={(e) => setS("mod_new_account_review", e.target.checked)} className="w-5 h-5 accent-[#d4af37]" />
          </label>
          <div>
            <label className="block text-sm text-white/70 mb-1">Posts max / minute (anti-flood)</label>
            <input type="number" min={1} max={20} value={settings.mod_max_posts_per_minute ?? 3} onChange={(e) => setS("mod_max_posts_per_minute", parseInt(e.target.value) || 3)}
              className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-sm text-white/70 mb-1">Score de confiance pour publication instantanée</label>
            <input type="number" min={0} max={100} value={settings.mod_trust_autopublish ?? 50} onChange={(e) => setS("mod_trust_autopublish", parseInt(e.target.value) || 50)}
              className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm" />
          </div>
          <div>
            <label className="block text-sm text-white/70 mb-1">Mots-clés bloqués (séparés par des virgules)</label>
            <textarea data-testid="set-blocked-keywords" rows={2} value={(settings.mod_blocked_keywords || []).join(", ")}
              onChange={(e) => setS("mod_blocked_keywords", e.target.value.split(",").map((x) => x.trim()).filter(Boolean))}
              className="w-full bg-white/[0.04] border border-white/10 rounded-lg px-3 py-2 text-sm resize-none" />
          </div>
          <button data-testid="save-mod-settings" onClick={saveSettings} className="gold-gradient text-black rounded-full px-5 py-2.5 text-sm font-medium">Enregistrer les réglages</button>
        </div>
      )}
    </div>
  );
}
